// The contract of `subscribeRedeemCode` (#6984): the Subscribe page's one code field
// sends a friend's code or an access code here, and the server says what happened.
// Sale codes never come here; they go through the sale lookup on `storeGetCheckout`.
// See design-docs/subscribe-code-field.md.

export interface SubscribeRedeemCodeParams {
  /** The code as typed. The server classifies it again; it never trusts the client's reading. */
  code: string;
}

/** What happened to a code typed into the Subscribe page's field. One message per value. */
export enum SubscribeRedeemOutcome {
  /** A friend's code was claimed; the checkout now offers the free month. */
  FriendApplied = 'FriendApplied',
  /** An access code was redeemed (or an earlier attempt of this same redemption was). */
  AccessRedeemed = 'AccessRedeemed',
  /** No such code, of any kind. Deliberately the same answer for every kind. */
  NotRecognised = 'NotRecognised',
  /** The friend offer is for new accounts: this one is too old, has subscribed, or has used a trial. */
  NewAccountsOnly = 'NewAccountsOnly',
  /** The caller typed their own friend code. */
  OwnCode = 'OwnCode',
  /** The caller was already invited by somebody else. */
  AlreadyReferred = 'AlreadyReferred',
  /** The access code was used before. */
  AlreadyUsed = 'AlreadyUsed',
  /** The access code would give this account nothing it does not already have. */
  AddsNothing = 'AddsNothing',
  /** The access code starts later; see `notValidForDays`. */
  NotValidYet = 'NotValidYet',
  /** Something went wrong on our side; trying again may work. */
  TryAgain = 'TryAgain',
}

export interface SubscribeRedeemCodeResult {
  outcome: SubscribeRedeemOutcome;
  /** For NotValidYet: whole days until the access code can be used. */
  notValidForDays?: number;
}
